import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState('free');

  useEffect(() => {
    // Check for existing session
    const savedUser = localStorage.getItem('bq_user');
    const savedSubscription = localStorage.getItem('bq_subscription');
    
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      setSubscription(savedSubscription || 'free');
    }
    setLoading(false);
  }, []);

  const loginWithEmail = async (email, password) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userData = {
        id: Date.now().toString(),
        email,
        name: email.split('@')[0],
        provider: 'email',
        avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${email}`,
        createdAt: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('bq_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      // Simulate Google OAuth
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const userData = {
        id: Date.now().toString(),
        email: 'user@gmail.com',
        name: 'Demo User',
        provider: 'google',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=google',
        createdAt: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('bq_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithFacebook = async () => {
    setLoading(true);
    try {
      // Simulate Facebook OAuth
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const userData = {
        id: Date.now().toString(),
        email: 'user@facebook.com',
        name: 'Demo User',
        provider: 'facebook',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=facebook',
        createdAt: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('bq_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userData = {
        id: Date.now().toString(),
        email,
        name,
        provider: 'email',
        avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${name}`,
        createdAt: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('bq_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setSubscription('free');
    localStorage.removeItem('bq_user');
    localStorage.removeItem('bq_subscription');
    localStorage.removeItem('projectData');
  };

  const upgradeToProfessional = async () => {
    setLoading(true);
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubscription('professional');
      localStorage.setItem('bq_subscription', 'professional');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    subscription,
    loading,
    loginWithEmail,
    loginWithGoogle,
    loginWithFacebook,
    register,
    logout,
    upgradeToProfessional,
    isAuthenticated: !!user,
    isProfessional: subscription === 'professional'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};